/**
 * RecentReportsCard - Displays the most recent risk reports
 * 
 * Features:
 * - Click to navigate to risk detail
 * - Shows type badge, severity, status, site, and time ago
 * - View All button to navigate to full list
 */
import React from 'react';

// Status display configuration
const STATUS_CONFIG = {
  NEW: { label: 'New', color: '#3b82f6', bg: '#eff6ff' },
  TRIAGED: { label: 'Triaged', color: '#8b5cf6', bg: '#f5f3ff' },
  IN_TREATMENT: { label: 'In Treatment', color: '#f59e0b', bg: '#fffbeb' },
  VERIFICATION: { label: 'Verification', color: '#06b6d4', bg: '#ecfeff' },
  CLOSED: { label: 'Closed', color: '#10b981', bg: '#ecfdf5' },
  ARCHIVED: { label: 'Archived', color: '#6b7280', bg: '#f3f4f6' },
};

// Severity display configuration
const SEVERITY_CONFIG = {
  LOW: { label: 'Low', color: '#10b981', bg: '#ecfdf5' },
  MEDIUM: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  HIGH: { label: 'High', color: '#f97316', bg: '#fff7ed' },
  CRITICAL: { label: 'Critical', color: '#ef4444', bg: '#fef2f2' },
};

// Report types configuration
const REPORT_TYPES = [
  { id: 'HAZARD', label: 'Hazard', icon: '/icons/hazard.png', color: '#f59e0b', emoji: '⚠️' },
  { id: 'NEAR_MISS', label: 'Near Miss', icon: '/icons/warning.png', color: '#8b5cf6', emoji: '⚡' },
  { id: 'INCIDENT', label: 'Incident', icon: '/icons/danger.png', color: '#ef4444', emoji: '🔴' },
  { id: 'RISK', label: 'Risk', icon: '/icons/risk_assessment.png', color: '#3b82f6', emoji: '📊' },
  { id: 'IMPROVEMENT', label: 'Improvement', icon: null, emoji: '💡', color: '#10b981' },
  { id: 'PSYCHOSOCIAL', label: 'Psychosocial', icon: '/icons/staff_welfare.png', color: '#722F37', emoji: '🧠' },
  { id: 'WELFARE', label: 'Welfare', icon: '/icons/staff_welfare.png', color: '#ec4899', emoji: '💜' },
];

// Status Badge
const StatusBadge = ({ status, size = 'sm' }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span 
      className={`status-badge status-badge-${size}`}
      style={{ color: config.color, backgroundColor: config.bg }}
    >
      {config.label}
    </span>
  );
};

// Severity Badge
const SeverityBadge = ({ severity }) => {
  if (!severity) {
    return <span className="severity-badge severity-unrated">Not assessed</span>;
  }
  const config = SEVERITY_CONFIG[severity] || { label: severity, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span 
      className="severity-badge"
      style={{ color: config.color, backgroundColor: config.bg }}
    >
      {config.label}
    </span>
  );
};

// Type Badge
const TypeBadge = ({ type }) => {
  const config = REPORT_TYPES.find(t => t.id === type) || { label: type, icon: null, emoji: '📄', color: '#6b7280' };
  return (
    <span className="type-badge" style={{ borderColor: config.color }}>
      {config.icon ? (
        <img src={config.icon} alt="" className="type-icon-img" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
      ) : (
        <span className="type-icon">{config.emoji || '📄'}</span>
      )}
      <span style={{ color: config.color }}>{config.label}</span>
    </span>
  );
};

// Time ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

export const RecentReportsCard = ({ recentRisks, onRiskClick, onViewAllClick }) => {
  const risks = recentRisks ?? [];
  
  return (
    <div className="dashboard-card dashboard-card-wide" data-testid="recent-reports-card">
      <div className="card-header-row">
        <h3>Recent Reports</h3>
        <button 
          className="btn btn-ghost btn-sm"
          onClick={onViewAllClick}
          data-testid="view-all-reports-btn"
        >
          View All →
        </button>
      </div>
      <div className="recent-risks-list">
        {risks.length > 0 ? (
          risks.map(risk => (
            <div 
              key={risk.id} 
              className="recent-risk-item"
              onClick={() => onRiskClick?.(risk.id)}
              data-testid={`recent-risk-${risk.id}`}
            >
              <div className="recent-risk-main">
                <TypeBadge type={risk.report_type} />
                <span className="recent-risk-title">{risk.title}</span>
              </div>
              <div className="recent-risk-meta">
                <span className="recent-risk-site">{risk.site_name || 'Unknown Site'}</span>
                <SeverityBadge severity={risk.severity} />
                <StatusBadge status={risk.status} size="xs" />
                <span className="recent-risk-time">{timeAgo(risk.created_at)}</span>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">No recent reports</p>
        )}
      </div>
    </div>
  );
};

export default RecentReportsCard;
